import { startCase } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import DarkMode from '@/assets/images/dark-mode.png';
import LightMode from '@/assets/images/light-mode.png';
import SystemMode from '@/assets/images/system-mode.png';
import { Box, XStack, YStack } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/Input';
import { changelogSeenStore } from '@/features/changelog';
import { FEATURES, useFeature } from '@/features/feature-flags';
import {
  SHELL_THEMES,
  ShellThemeSwatch,
  type ShellTheme,
} from '@/features/shell-theme';
import {
  DEFAULT_TERMINAL_FONT_SIZE,
  TERMINAL_FONT_SIZE_RANGE,
  terminalSettingsStore,
} from '@/features/sprites/terminal-settings.store';
import { tabStore } from '@/features/tabs/tabs.store';
import { cn } from '@/lib/utils';
import { settingsStore, TypeScale } from '@/stores/settings.store';
import { ChatWidth, uiStore } from '@/stores/ui.store';
import { userStore } from '@/stores/user.store';
import { inElectron, isMac } from '@/utils/capabilities';

import { SettingsSection } from '../settings-section';

import { AppIconPicker } from './app-icon-picker';

export const AppearanceSettings = () => {
  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Appearance</SettingsSection.Title>
      </SettingsSection.Header>
      <SettingsSection.Item
        direction="vertical"
        searchId="appearance.color-mode"
        className="gap-4"
      >
        <SettingsSection.GroupHeader>Color Mode</SettingsSection.GroupHeader>
        <ThemeToggle />
      </SettingsSection.Item>
      <SettingsSection.Item
        direction="vertical"
        searchId="appearance.theme"
        className="gap-4"
      >
        <SettingsSection.GroupHeader>Theme</SettingsSection.GroupHeader>
        <ShellThemePicker />
      </SettingsSection.Item>
      {isMac() && <AppIconPicker />}
      <SettingsSection.Group>
        <RailLabels />
        <SettingsSection.Seperator />
        <SidebarWhatsNew />
        <SettingsSection.Seperator />
        <AccentButtons />
        <SettingsSection.Seperator />
        <WideChat />
        <SettingsSection.Seperator />
        <FontSizeSelector />
        <SettingsSection.Seperator />
        <DyslexicFont />
        <SettingsSection.Seperator />
        <WrapCodeBlock />
      </SettingsSection.Group>
      <HideInternalTools />
      <TabsAppearance />
      <TerminalAppearance />
    </SettingsSection.Root>
  );
};

const TabsAppearance = () => {
  if (!inElectron()) return null;
  return (
    <SettingsSection.Item direction="vertical" className="gap-4">
      <SettingsSection.GroupHeader>Tabs</SettingsSection.GroupHeader>
      <SettingsSection.Group>
        <CloseIdleTabs />
      </SettingsSection.Group>
    </SettingsSection.Item>
  );
};

const formatIdleTabsSetting = (days: number | null) => {
  if (days === null) return 'Never';
  return days === 1 ? 'After 1 day' : `After ${days} days`;
};

const CloseIdleTabs = observer(() => (
  <SettingsSection.Item searchId="appearance.close-idle-tabs">
    <SettingsSection.ItemHeader>
      <SettingsSection.ItemLabel>Close Idle Tabs</SettingsSection.ItemLabel>
      <SettingsSection.ItemDescription>
        Close tabs you haven&apos;t opened in a while, checked when you open a
        new tab. Pinned tabs stay open.
      </SettingsSection.ItemDescription>
    </SettingsSection.ItemHeader>
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <span>{formatIdleTabsSetting(tabStore.closeIdleTabsAfterDays)}</span>
        <Icons.chevronDown className="size-3" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="end"
        side="bottom"
        className="w-40"
        sideOffset={8}
      >
        {[1, 3, 7, 30, null].map((days) => (
          <DropdownMenu.Item
            key={days ?? 'never'}
            onSelect={() => tabStore.setCloseIdleTabsAfterDays(days)}
          >
            {formatIdleTabsSetting(days)}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  </SettingsSection.Item>
));

const TerminalAppearance = observer(() => {
  const hasFeature = useFeature();
  if (!inElectron() || !hasFeature(FEATURES.LOCAL_TERMINAL)) return null;
  return (
    <SettingsSection.Item
      direction="vertical"
      searchId="appearance.terminal"
      className="gap-4"
    >
      <SettingsSection.GroupHeader>Terminal</SettingsSection.GroupHeader>
      <SettingsSection.Group>
        <TerminalFontFamily />
        <SettingsSection.Seperator />
        <TerminalFontSize />
      </SettingsSection.Group>
    </SettingsSection.Item>
  );
});

/** Commits on blur/Enter and redisplays the stored value, like the Voice numeric inputs. */
const TerminalFontFamily = observer(() => {
  const stored = terminalSettingsStore.fontFamily;
  const [text, setText] = useState(stored);
  useEffect(() => setText(stored), [stored]);

  const commit = () => terminalSettingsStore.setFontFamily(text);

  return (
    <SettingsSection.Item searchId="appearance.terminal-font">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Font</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Monospace family for the terminal. Leave empty to use an installed
          Nerd Font if present, otherwise the system monospace.
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <Box className="w-64 shrink-0">
        <Input
          inputSize="sm"
          aria-label="Terminal font family"
          placeholder="e.g. JetBrainsMono Nerd Font Mono"
          spellCheck={false}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
        />
      </Box>
    </SettingsSection.Item>
  );
});

const TerminalFontSize = observer(() => {
  const stored = `${terminalSettingsStore.fontSize}`;
  const [text, setText] = useState(stored);
  useEffect(() => setText(stored), [stored]);

  const commit = () => {
    const size = Number.parseFloat(text);
    if (Number.isFinite(size)) terminalSettingsStore.setFontSize(size);
    setText(`${terminalSettingsStore.fontSize}`);
  };

  return (
    <SettingsSection.Item searchId="appearance.terminal-font-size">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Font Size</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          {`Terminal text size in px (${TERMINAL_FONT_SIZE_RANGE.min}-${TERMINAL_FONT_SIZE_RANGE.max}, default ${DEFAULT_TERMINAL_FONT_SIZE})`}
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <Box className="w-[5.25rem] shrink-0">
        <Input
          type="number"
          inputMode="numeric"
          inputSize="sm"
          className="text-right"
          aria-label="Terminal font size"
          min={TERMINAL_FONT_SIZE_RANGE.min}
          max={TERMINAL_FONT_SIZE_RANGE.max}
          step={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
        />
      </Box>
    </SettingsSection.Item>
  );
});

const ThemeToggle = observer(() => {
  const isLight = uiStore.themePreference === 'light';
  const isDark = uiStore.themePreference === 'dark';
  const isSystem = uiStore.themePreference === 'system';

  return (
    <XStack className="gap-6">
      <ThemeCard
        label="Light"
        image={LightMode}
        selected={isLight}
        onSelect={() => uiStore.setThemePreference('light')}
      />
      <ThemeCard
        label="Dark"
        image={DarkMode}
        selected={isDark}
        onSelect={() => uiStore.setThemePreference('dark')}
      />
      <ThemeCard
        label="Match System"
        image={SystemMode}
        selected={isSystem}
        onSelect={() => uiStore.setThemePreference('system')}
      />
    </XStack>
  );
});

type ThemeCardProps = {
  label: string;
  image: string;
  selected: boolean;
  onSelect: () => void;
};

const ThemeCard = ({ label, image, selected, onSelect }: ThemeCardProps) => {
  return (
    <YStack className="gap-2 items-start">
      <YStack
        onClick={onSelect}
        className={cn(
          'bg-surface-default border border-border-strong rounded-xl overflow-clip',
          selected && 'bg-surface-active border-accent'
        )}
      >
        {/* Explicit dimensions reserve the aspect-ratio box before the image
            loads, preventing a layout shift when the tab is opened */}
        <img src={image} alt={label} width={1163} height={653} />
      </YStack>
      <p className="px-2 text-sm">{label}</p>
    </YStack>
  );
};

const ShellThemePicker = observer(() => (
  <div className="grid w-full grid-cols-2 gap-3 md:grid-cols-4">
    {SHELL_THEMES.map((preset) => (
      <ShellThemeCard
        key={preset}
        preset={preset}
        selected={uiStore.shellTheme === preset}
        onSelect={() => uiStore.setShellTheme(preset)}
      />
    ))}
  </div>
));

type ShellThemeCardProps = {
  preset: ShellTheme;
  selected: boolean;
  onSelect: () => void;
};

const ShellThemeCard = ({
  preset,
  selected,
  onSelect,
}: ShellThemeCardProps) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onSelect}
    className={cn(
      'flex items-center gap-4 rounded-xl border border-border-strong bg-surface-default px-4 py-3 text-left focus-ring transition-colors hover:bg-surface-hover',
      selected && 'border-accent bg-surface-active'
    )}
  >
    <ShellThemeSwatch preset={preset} className="size-12 shrink-0" />
    <span className="text-sm font-medium">{startCase(preset)}</span>
  </button>
);

const FontSizeSelector = observer(() => {
  const selectedTypeScale =
    Object.entries(TypeScale).find(
      ([, enumValue]) => enumValue === settingsStore.typeScale
    )?.[0] ?? 'Normal';

  return (
    <SettingsSection.Item searchId="appearance.font-size">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Font Size</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Adjust the size of text across the app
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <span>{startCase(selectedTypeScale)}</span>
          <Icons.chevronDown className="size-3" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          side="bottom"
          className="w-40"
          sideOffset={8}
        >
          {Object.entries(TypeScale).map(([name, scale]) => (
            <DropdownMenu.Item
              key={name}
              onSelect={() => settingsStore.setTypeScale(scale)}
            >
              {startCase(name)}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
});

const DyslexicFont = observer(() => {
  return (
    <SettingsSection.Item searchId="appearance.dyslexic-font">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Dyslexia-Friendly Font
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Use OpenDyslexic, a typeface designed for readers with dyslexia
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SettingsSection.Switch
        checked={settingsStore.dyslexicFont}
        onCheckedChange={(enabled) => settingsStore.setDyslexicFont(enabled)}
      />
    </SettingsSection.Item>
  );
});

const WrapCodeBlock = observer(() => {
  return (
    <SettingsSection.Item searchId="appearance.code-block-wrapping">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Code Block Wrapping
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Enable or disable code block wrapping
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SettingsSection.Switch
        checked={settingsStore.codeBlockLineWrap}
        onCheckedChange={(v) => settingsStore.setCodeBlockLineWrap(v)}
      />
    </SettingsSection.Item>
  );
});

const RailLabels = observer(() => {
  return (
    <SettingsSection.Item searchId="appearance.rail-labels">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Show Labels in Icon Sidebar
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Show a label under each icon in the icon sidebar
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SettingsSection.Switch
        checked={uiStore.navRailLabelsVisible}
        onCheckedChange={(show) => uiStore.setNavRailLabelsVisible(show)}
      />
    </SettingsSection.Item>
  );
});

const SidebarWhatsNew = observer(() => {
  return (
    <SettingsSection.Item searchId="appearance.sidebar-whats-new">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Show What's New in Sidebar
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Show new changelog entries at the bottom of the sidebar
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SettingsSection.Switch
        checked={!changelogSeenStore.isSidebarHidden}
        onCheckedChange={(enabled) =>
          changelogSeenStore.setSidebarHidden(!enabled)
        }
      />
    </SettingsSection.Item>
  );
});

const AccentButtons = observer(() => {
  return (
    <SettingsSection.Item searchId="appearance.accent-buttons">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Accent Buttons</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Use the theme color for primary buttons
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SettingsSection.Switch
        checked={uiStore.shellAccentButtons}
        onCheckedChange={(enabled) => uiStore.setShellAccentButtons(enabled)}
      />
    </SettingsSection.Item>
  );
});

const HideInternalTools = observer(() => {
  if (!userStore.hasInternalAccess) return null;

  return (
    <SettingsSection.Group>
      <SettingsSection.Item searchId="appearance.hide-internal-tools">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>
            Hide Internal Tools
          </SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Hide internal-only menus, modes and debug views, as customers see
            the app
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <SettingsSection.Switch
          checked={userStore.hideInternalTools}
          onCheckedChange={(checked) => userStore.setHideInternalTools(checked)}
        />
      </SettingsSection.Item>
    </SettingsSection.Group>
  );
});

const WideChat = observer(() => {
  return (
    <SettingsSection.Item searchId="appearance.extra-wide-chat">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Extra Wide Chat</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Choose whether to make the chat extra wide
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SettingsSection.Switch
        checked={uiStore.chatWidth === ChatWidth.Wide}
        onCheckedChange={() => uiStore.toggleChatFullWidth()}
      />
    </SettingsSection.Item>
  );
});
